/**
 * ReportProblemDialog
 *
 * One "describe what went wrong" modal for every surface that has something worth complaining about —
 * an email thread, a chat session, a suggested quote. It owns the textarea, the submitting state, the
 * snackbar and dismiss-on-backdrop; the CALLER owns what a report means and where it goes, as `onSubmit`.
 *
 * It lives here rather than in one feature because the second consumer made the payload the only thing
 * the three had in common, and binding this to any one of them would have had the next surface copy the
 * dialog to change its request. What it hands over is the typed message and, where the surface offers a
 * list to pick from, which item was picked.
 *
 * That list is the caller's: this file knows that reports can have a reason and that one of them may
 * demand words, and nothing about quotes, lanes or threads. A surface that passes no list behaves exactly
 * as it did when the box was the whole dialog.
 */

import { useCallback, useEffect, useState } from 'react'
import { showError, showSuccess } from 'utils/snackbar'
import { getUserErrorMessage } from 'utils/get-user-error-message'

/**
 * One choice on the pick-one list above the box.
 *
 * Generic in its value so a caller's own union reaches its `onSubmit` unchanged. Widened to `string`
 * here, the picked value would come back as a `string` the feature has to cast back into its contract —
 * an unchecked cast, which is exactly the claim it looks like it is proving. A surface that passes no
 * list infers `string` and never notices the parameter.
 */
export interface ReportProblemReason<T extends string = string> {
    value: T
    label: string
    /**
     * This choice means "none of the others", so the words ARE the report and the box stops being
     * optional. Submitting stays disabled until there are some.
     */
    requiresNote?: boolean
}

interface ReportProblemDialogProps<T extends string = string> {
    open: boolean
    onClose: () => void
    /**
     * Send the report. Rejecting keeps the dialog open with the text and the choice intact, so nothing
     * the person did is lost.
     *
     * `reason` is the picked value, and `undefined` on a surface that offers no list.
     */
    onSubmit: (message: string, reason?: T) => Promise<unknown>
    /** Title rendered inside the dialog. Defaults to 'Report a problem'. */
    title?: string
    /** Subtitle / helper text under the title. Defaults to a generic message. */
    description?: string
    /** Placeholder shown in the textarea. */
    placeholder?: string
    /** Snackbar text on success. */
    successMessage?: string
    /**
     * The reasons this surface can be complained about, in the order they are offered.
     *
     * Where it is given, picking one is required and the box is the optional half — a named reason is a
     * complete report, and prose about the same handful of things cannot be counted. Where it is left
     * out, the box is the report, as before.
     */
    reasonOptions?: readonly ReportProblemReason<T>[]
    /**
     * Hard stop on the box, for a surface whose backend truncates.
     *
     * Without it a paste longer than the server's cap is accepted here, cut there, and still reported as
     * sent — the person never learns which half arrived. Left unset where the backend takes whatever it
     * is given.
     */
    maxLength?: number
}

export const ReportProblemDialog = <T extends string = string>({
    open,
    onClose,
    onSubmit,
    title = 'Report a problem',
    description = 'Describe what went wrong. The team will review and follow up.',
    placeholder = 'What happened?',
    successMessage = 'Problem reported',
    reasonOptions,
    maxLength,
}: ReportProblemDialogProps<T>) => {
    const [text, setText] = useState('')
    const [reason, setReason] = useState<T>()
    const [submitting, setSubmitting] = useState(false)

    /** Only `OTHER`-shaped choices demand words; everything else leaves the box to the person. */
    const noteRequired = reasonOptions?.some((option) => option.value === reason && option.requiresNote) ?? false
    const message = text.trim()
    // On `length`, not on the prop: an empty array is truthy, and a caller that filters its list down to
    // nothing would get a dialog with no chips and a Report button that can never be pressed — not even
    // on typed prose, which is the behaviour it should have fallen back to.
    const offersReasons = Boolean(reasonOptions?.length)
    const canSubmit = offersReasons ? Boolean(reason) && (!noteRequired || Boolean(message)) : Boolean(message)

    /**
     * Why Report is disabled, in the two states where the person has already done something and is
     * waiting for the button to work. The untouched dialog gets nothing — a hint on an empty form is
     * noise, and the chips are visibly unpicked.
     *
     * The second case is the commoner one, and it is the old habit: the box used to BE the report, so
     * someone types their sentence first and never looks up at the chips.
     */
    const blockedHint =
        noteRequired && !message
            ? 'Tell us what is wrong, so there is a report to act on.'
            : offersReasons && !reason && message
              ? 'Pick what is wrong above.'
              : undefined

    const handleSubmit = useCallback(async () => {
        if (!canSubmit) return
        setSubmitting(true)
        try {
            await onSubmit(message, reason)
            showSuccess(successMessage)
            onClose()
            setText('')
            setReason(undefined)
        } catch (error) {
            // Left open on purpose. A failed report is one the person still has to send, and clearing
            // the box would make them type it again.
            showError(getUserErrorMessage(error))
        } finally {
            setSubmitting(false)
        }
    }, [canSubmit, message, reason, onSubmit, successMessage, onClose])

    /**
     * Escape closes THIS dialog and nothing behind it.
     *
     * Captured on the document rather than handled on the dialog's own element, because what it has to
     * beat is a document-level listener: the Box Truck load drawer closes itself on Escape, so a
     * dispatcher pressing it mid-sentence closed the drawer, unmounted the dialog with it, and lost what
     * they had typed — the exact loss this dialog otherwise guards against. The capture phase runs before
     * any bubble-phase listener whatever holds focus, so a click on the backdrop does not reopen the hole.
     *
     * Ignored while submitting: the send is already out, and closing on top of it would hide its result.
     */
    useEffect(() => {
        if (!open) return
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return
            event.stopPropagation()
            if (!submitting) onClose()
        }
        document.addEventListener('keydown', onKeyDown, true)
        return () => document.removeEventListener('keydown', onKeyDown, true)
    }, [open, submitting, onClose])

    if (!open) return null

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => !submitting && onClose()}>
            <div
                className="w-[420px] max-w-[92vw] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-4"
                onClick={(e) => e.stopPropagation()}
            >
                <h3 className="text-[14px] font-semibold text-slate-900 dark:text-slate-100 mb-1">{title}</h3>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3">{description}</p>
                {/* Pick-one, with no way back to nothing picked: a dead Report button with every chip grey
                    says less about what is missing than one already chosen says about what is being sent.
                    Guarded on the prop itself, not on the `offersReasons` boolean above it: the boolean
                    is the same answer, but it is not a type guard, so `reasonOptions` stays possibly
                    undefined inside this block. A ternary and not `&&`, because `0 &&` renders a 0. */}
                {reasonOptions?.length ? (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                        {reasonOptions.map((option) => {
                            const picked = option.value === reason
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => setReason(option.value)}
                                    disabled={submitting}
                                    aria-pressed={picked}
                                    className={`h-6 px-2 rounded-full border text-[11px] font-medium transition-colors disabled:opacity-50 ${
                                        picked
                                            ? 'bg-amber-500 border-amber-500 text-white'
                                            : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-amber-400'
                                    }`}
                                >
                                    {option.label}
                                </button>
                            )
                        })}
                    </div>
                ) : null}
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={placeholder}
                    rows={4}
                    maxLength={maxLength}
                    disabled={submitting}
                    className="w-full text-[12px] bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2.5 py-2 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-blue-600 resize-none"
                />
                {/* Said out loud, rather than left for a disabled button to imply. */}
                {blockedHint && <p className="mt-1.5 text-[10px] text-amber-600 dark:text-amber-500">{blockedHint}</p>}
                <div className="flex items-center justify-end gap-2 mt-3">
                    {/* Only once it is close enough to matter — a counter on an empty box is noise. */}
                    {maxLength !== undefined && text.length > maxLength * 0.8 && (
                        <span className="mr-auto text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
                            {text.length} / {maxLength}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        className="h-7 px-3 rounded-md text-[12px] font-medium text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting || !canSubmit}
                        className="h-7 px-3 rounded-md text-[12px] font-medium bg-amber-500 hover:bg-amber-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? 'Reporting…' : 'Report'}
                    </button>
                </div>
            </div>
        </div>
    )
}
